import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { rpcClient } from 'src/api/rpc/client';
import { Input } from 'src/components/ui/input';
import { toast } from 'src/components/ui/toast';
import { lookupTaskForGoTo } from './taskGoToId';

/**
 * Jump box on task queues: paste `AP-1234` (or a UUID) and open that task.
 */
export function TaskGoToIdField() {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();
  const gt = useGT();

  async function goToId() {
    setPending(true);
    const result = await lookupTaskForGoTo(value, (id) =>
      rpcClient.tasks.get({ id }),
    ).catch(() => null);
    if (result == null) {
      toast.error(
        gt('Could not open that task', {
          context:
            'error toast when go-to-id on a tasks page fails to load the task',
        }),
      );
      setPending(false);
      return;
    }
    if (result.kind === 'invalid') {
      toast.error(
        gt('Enter a task ID like AP-1234', {
          context:
            'error toast when the go-to-id field on a tasks page is empty or not a task id',
        }),
      );
      setPending(false);
      return;
    }
    if (result.kind === 'missing') {
      toast.error(
        gt('No task found for {id}', {
          context:
            'error toast when go-to-id on a tasks page does not match a task',
          id: result.id,
        }),
      );
      setPending(false);
      return;
    }
    setValue('');
    setPending(false);
    await navigate({
      to: '/tasks/$taskId',
      params: { taskId: result.id },
    });
  }

  return (
    <form
      className="contents"
      onSubmit={(event) => {
        event.preventDefault();
        void goToId();
      }}
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={gt('Go to ID…', {
          context: 'placeholder for the jump-to-task-id field on tasks pages',
        })}
        aria-label={gt('Go to task ID', {
          context:
            'accessible name for the jump-to-task-id field on tasks pages',
        })}
        disabled={pending}
        className="h-8 w-36"
      />
    </form>
  );
}
